import classNames from 'classnames';
import { useState } from 'react';
import Stack from '@mui/material/Stack';

import { removeViewedVehicleItemLocalStorage } from '../../../../pages/home/utilities/viewedVehiclesTileBuilder';
import { ITrackingPayload } from '../../../../types/analytics';
import { IVehicleTile } from '../../../../types/vehicleTile';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../../../utilities/analytics';
import { fetchAndBuildRecommendedVehicleTiles } from '../../utilities/recommendationsBuilder';
import useAppState from '../../../../common/state/appState';
import HomebaseTab from '../store/HomebaseTab';
import RecentlyViewedCarousel from './carousels/RecentlyViewedCarousel';
import RecommendedCarousel from './carousels/RecommendedCarousel';
import SearchedCarousel from './carousels/SearchedCarousel';
import { HomeBaseTabList } from './HomeBaseTabList';

interface IHomeBaseTabsProps {
    onOpenSnackBar: (text: string) => void;
}

const HomeBaseTabs = ({ onOpenSnackBar }: IHomeBaseTabsProps) => {
    const [hasLoadedRecommendedTab, setHasLoadedRecommendedTab] = useState(false);
    const [isLoadingRecommendedTab, setIsLoadingRecommendedTab] = useState(false);
    const [recommendedVehicleList, setRecommendedVehicleList] = useState<IVehicleTile[]>([]);

    const activeTab = useAppState((state) => state.homeBaseTab);
    const setHomeBaseTab = useAppState((state) => state.setHomeBaseTab);
    const viewedVehicles = useAppState((state) => state.recentlyViewedVehicles);
    const removeRecentlyViewedVehicle = useAppState((state) => state.removeRecentlyViewedVehicle);
    const favoritedStockNumbers = useAppState((state) => state.favoriteVehicles);
    const isLoadingRecentSearches = useAppState((state) => state.isLoadingRecentSearches);
    const hasLoadedRecentSearches = useAppState((state) => state.hasLoadedRecentSearches);
    const recentSearches = useAppState((state) => state.recentSearches);
    const loadRecentSearches = useAppState((state) => state.loadRecentSearches);

    const loadRecommendationsTab = () => {
        setIsLoadingRecommendedTab(true);
        fetchAndBuildRecommendedVehicleTiles(favoritedStockNumbers, viewedVehicles).then((recommendedVehicleTiles) => {
            setIsLoadingRecommendedTab(false);
            setHasLoadedRecommendedTab(true);
            setRecommendedVehicleList(recommendedVehicleTiles.vehicleTiles);
        });
    };

    const handleTabChange = (event: React.SyntheticEvent, nextTabId: number) => {
        const label = (event.currentTarget as HTMLElement).innerText;
        if (nextTabId === HomebaseTab.Searched && !hasLoadedRecentSearches) {
            loadRecentSearches();
        } else if (nextTabId === HomebaseTab.Recommended && !hasLoadedRecommendedTab) {
            loadRecommendationsTab();
        }

        const payload: ITrackingPayload = {
            event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
            linkDetails: {
                name: label?.trimStart(),
                position: 'homepage',
            },
        };

        trackEDDL(payload);
        setHomeBaseTab(nextTabId);
    };

    const updateViewedTiles = (tileToRemove: IVehicleTile) => {
        removeViewedVehicleItemLocalStorage(tileToRemove);
        removeRecentlyViewedVehicle(tileToRemove.stockNumber);
    };

    return (
        <Stack
            className="home-base-hero--main-content home-base-hero--personalized-tabs"
            spacing={3}
            sx={{
                px: {
                    xs: 0,
                    sm: 4,
                    md: 6,
                },
            }}
        >
            <HomeBaseTabList onChange={handleTabChange} selectedTabId={activeTab} />
            <div
                className={classNames('hp-tab-content', {
                    active: activeTab === HomebaseTab.RecentlyViewed,
                    hidden: activeTab !== HomebaseTab.RecentlyViewed,
                })}
                id="kmx-hb-tab-panel-0"
                role="tabpanel"
                aria-labelledby="kmx-hb-tab-0"
                aria-label="Recently Viewed Vehicles"
            >
                <RecentlyViewedCarousel
                    onOpenSnackBar={onOpenSnackBar}
                    isVisible={activeTab === HomebaseTab.RecentlyViewed}
                    viewedVehicleList={viewedVehicles}
                    updatedViewedTiles={updateViewedTiles}
                />
            </div>
            <div
                className={classNames('hp-tab-content', {
                    active: activeTab === HomebaseTab.Searched,
                    hidden: activeTab !== HomebaseTab.Searched,
                })}
                id="kmx-hb-tab-panel-1"
                role="tabpanel"
                aria-labelledby="kmx-hb-tab-1"
                aria-label="Recent Vehicle Searches"
            >
                <SearchedCarousel
                    isVisible={activeTab === HomebaseTab.Searched}
                    recentSearchesList={recentSearches}
                    isLoadingSearchedTab={isLoadingRecentSearches}
                    hasLoadedSearchedTab={hasLoadedRecentSearches}
                    leftAligned
                />
            </div>
            <div
                className={classNames('hp-tab-content', {
                    active: activeTab === HomebaseTab.Recommended,
                    hidden: activeTab !== HomebaseTab.Recommended,
                })}
                id="kmx-hb-tab-panel-2"
                role="tabpanel"
                aria-labelledby="kmx-hb-tab-2"
                aria-label="Recommended Vehicles"
            >
                <RecommendedCarousel
                    hasLoadedRecommendedTab={hasLoadedRecommendedTab}
                    recommendedVehicleList={recommendedVehicleList}
                    isVisible={activeTab === HomebaseTab.Recommended}
                    favoritedStockNumberList={favoritedStockNumbers}
                    isLoadingRecommendedTab={isLoadingRecommendedTab}
                    onOpenSnackBar={onOpenSnackBar}
                />
            </div>
        </Stack>
    );
};

export default HomeBaseTabs;
// This Code has to be updated in order to fix the animation between tabs
// private initActiveStateIndicator() {
//     const refPositionInfo = this.state.defaultTabRef.current.getBoundingClientRect();

//     // set initial dimensions and position
//     gsap.set(this.animatedActiveStateIndicator.current, {
//         width: refPositionInfo.width,
//         x: refPositionInfo.x,
//         display: 'none',
//     });
// }

// private transitionPillActiveState(ref: any, nextTabId: number) {
//     const newTabPositionInfo = ref.current.getBoundingClientRect();
//     const currentTabPositionInfo = this.state.activeTabRef.current.getBoundingClientRect();
//     const activeStateIndicator = this.animatedActiveStateIndicator.current;

//     if (window.innerWidth < MOBILE_TABLET_BREAKPOINT) {
//         this.pillTransition = new TimelineMax({
//             onStart: () => {
//                 this.setState({
//                     pillCurrentlyTransitioning: true,
//                 });
//             },
//             onComplete: () => {
//                 this.setState(
//                     {
//                         pillCurrentlyTransitioning: false,
//                     },
//                     () => {
//                         TweenMax.set(activeStateIndicator, { display: 'none' });
//                     }
//                 );
//             },
//         });

//         // set dimensions and location of CURRENT active tab (start position of animation)
//         this.pillTransition.set(activeStateIndicator, {
//             width: currentTabPositionInfo.width,
//             x: currentTabPositionInfo.x,
//             display: 'block',
//         });

//         if (this.state.tabListOrder.indexOf(nextTabId) > this.state.activeTabIndex) {
//             // moving from left to right

//             // start transition of width
//             this.pillTransition.to(activeStateIndicator, 0.25, {
//                 width: currentTabPositionInfo.width + newTabPositionInfo.width,
//                 ease: 'power2.inOut',
//             });

//             // catch up x position during width transition (drag effect)
//             this.pillTransition.to(
//                 activeStateIndicator,
//                 0.25,
//                 {
//                     width: newTabPositionInfo.width,
//                     x: newTabPositionInfo.x,
//                     ease: 'power2.inOut',
//                 },
//                 '-=.125'
//             );
//         } else {
//             // moving from right to left

//             // start transition of width and x value
//             this.pillTransition.to(activeStateIndicator, 0.25, {
//                 width: currentTabPositionInfo.width + newTabPositionInfo.width,
//                 x: newTabPositionInfo.x,
//                 ease: 'power2.inOut',
//             });

//             // reduce width
//             this.pillTransition.to(
//                 activeStateIndicator,
//                 0.25,
//                 {
//                     width: newTabPositionInfo.width,
//                     ease: 'power2.inOut',
//                 },
//                 '-=.125'
//             );
//         }

//         // add timing buffer to end of timeline (dummy tween)
//         this.pillTransition.set({}, {}, 0.5);
//     }
// }
